import axios from "axios";
import { useAuthStore } from "../store/authStore";
import { getCSRFToken, clearCSRFToken } from "./csrf";

// If VITE_API_BASE_URL is set, use it. Otherwise use relative URL (for Vite proxy)
const API_URL = import.meta.env.VITE_API_BASE_URL;
const baseURL = API_URL ? `${API_URL}/api/v1` : "/api/v1";

export const api = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// State-changing methods that require CSRF protection
const CSRF_METHODS = ["POST", "PUT", "PATCH", "DELETE"];

// Request interceptor to add auth token and CSRF token
api.interceptors.request.use(
  async (config) => {
    // Add auth token if available
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add CSRF token for state-changing methods
    if (config.method && CSRF_METHODS.includes(config.method.toUpperCase())) {
      try {
        const csrfToken = await getCSRFToken();
        config.headers["X-CSRF-Token"] = csrfToken;
      } catch (error) {
        console.error("[API] Failed to get CSRF token:", error);
        // For auth endpoints, we'll let the request proceed and fail naturally
        // This allows better error messages from the server
        if (!config.url?.includes('/auth/')) {
          // For non-auth endpoints, this is a critical error
          throw new Error("Failed to obtain CSRF token. Please refresh the page and try again.");
        }
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh and CSRF errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle CSRF token errors - clear cache and retry once
    if (error.response?.status === 403 && error.response?.data?.error?.includes('CSRF')) {
      console.log("[API] CSRF error detected, clearing token cache");
      clearCSRFToken();

      // Retry the request once with a fresh token
      if (!originalRequest._retry) {
        originalRequest._retry = true;
        try {
          const csrfToken = await getCSRFToken();
          originalRequest.headers["X-CSRF-Token"] = csrfToken;
          return api(originalRequest);
        } catch (retryError) {
          console.error("[API] CSRF retry failed:", retryError);
        }
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("refresh_token");
        if (refreshToken) {
          const response = await axios.post(`${baseURL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { access_token, refresh_token: newRefreshToken } = response.data.data.tokens;
          localStorage.setItem("access_token", access_token);
          localStorage.setItem("refresh_token", newRefreshToken);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear auth and redirect to login
        const { clearAuth } = useAuthStore.getState();
        clearAuth();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
